import { Box, ContentDialog, LegacyContainer, SimpleWidgetCard, Stack } from "@tm/components"
import { useTelesalesCustomerNumber, useWorkTask } from "@tm/context-distribution"
import { WidgetSizes } from "@tm/controls"
import { useLocalization } from "@tm/localization"
import { ECounterType, VoucherType } from "@tm/models"
import {
    encodeUniqueId,
    mapVoucherTypeForUrl,
    ModulePropsMultiWidget,
    renderRoute,
    TmaHelper,
    uniqueId,
    useExternalCatalogUrl,
    useMultiWidgetState,
    useResizeObserver,
} from "@tm/utils"
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { useHistory, useParams } from "react-router"
import { useSetRecoilState } from "recoil"
import { voucherListFilterAtom } from "@bundles/vouchers/data/states/listFilters"
import { VoucherRouteParams } from "../../business"
import { useDefaultVoucherType } from "../../hooks/useDefaultVoucherType"
import { getBundleParams } from "../../utils"
import { WidgetSVariant } from "./WidgetSVariant"
import { VoucherWidgetHeader } from "./VoucherWidgetHeader"
import { VoucherWidgetFooter } from "./VoucherWidgetFooter"
import { VoucherWidgetContent } from "./VoucherWidgetContent"

type Props = ConfigurationProps & {
    className?: string
    showComponentKey?: string
    moduleProps?: ModulePropsMultiWidget
    defaultVoucherType: VoucherType
}

type ConfigurationProps = {
    externalSystemId?: number
    height?: number
    hideCover?: boolean
    initialVoucherType?: VoucherType
    showArticleNumberSearch?: boolean // widget's size must be 4x4
    showComponentKey?: string
    size?: WidgetSizes
    titleTextId?: number
}

function WidgetComponent(props: Props) {
    const {
        externalSystemId,
        showComponentKey,
        height,
        size,
        className,
        moduleProps,
        hideCover,
        initialVoucherType,
        defaultVoucherType,
        titleTextId,
        showArticleNumberSearch,
    } = props
    const { translateText } = useLocalization()
    const { workTask } = useWorkTask() ?? {}
    const matchParams = useParams<VoucherRouteParams>()
    const history = useHistory()
    const [active, setActive] = useState<boolean>(!!hideCover)
    const [voucherType, setVoucherType] = useState<VoucherType>(initialVoucherType || defaultVoucherType)
    const { telesalesCustomerNo, enableServiceCalls } = useTelesalesCustomerNumber()
    const { externalCatalogUrl, loadingExternalCatalogUrl } = useExternalCatalogUrl({ externalSystemId, telesalesCustomerNo })
    const [showName, setShowName] = useState(false)
    const [showSVariant, setShowSVariant] = useState(false)
    const [showExternalOrdersDialog, setShowExternalOrdersDialog] = useState(false)
    const [selectedMultiWidgetTab] = useMultiWidgetState(moduleProps)
    const url = useMemo(() => {
        const params = {
            ...matchParams,
            type: mapVoucherTypeForUrl(voucherType),
            workTaskId: matchParams.workTaskId ?? encodeUniqueId(uniqueId()),
        }
        return renderRoute(getBundleParams().vouchersListRoute, params)
    }, [matchParams, voucherType])
    const listActive = !matchParams.type || !matchParams.id
    const [widgetRef, unsubResizeObserver] = useResizeObserver<HTMLDivElement>(handleResizeContainer)
    const containerRef = useRef<HTMLDivElement | null>(null)
    const setListFilter = useSetRecoilState(voucherListFilterAtom(matchParams.workTaskId))
    const combinedRef = (node: HTMLDivElement) => {
        widgetRef(node)
        containerRef.current = node
    }

    useEffect(() => unsubResizeObserver, [unsubResizeObserver])

    useEffect(() => {
        if (workTask?.vehicle?.id) {
            setListFilter((prev) => ({
                ...prev,
                selectedVehicleId: workTask?.vehicle?.id,
            }))
        }
    }, [workTask?.vehicle?.id, setListFilter])

    useLayoutEffect(() => {
        const { current } = containerRef
        if (current) {
            updateVisibility(current.clientWidth, current.clientHeight)
        }
    }, [containerRef, showSVariant, showName])

    function updateVisibility(widgetWidth: number, widgetHeight: number) {
        setShowName(widgetWidth > 600)
        setShowSVariant(widgetWidth < 350 || widgetHeight < 250)
    }

    function handleOpenExternalDialog(toOpenType: VoucherType): boolean {
        if (externalSystemId && toOpenType === VoucherType.Order) {
            setShowExternalOrdersDialog(true)
            return true
        }
        return false
    }

    function handleCoverClick() {
        setActive(true)
    }

    function handleResizeContainer(entry?: ResizeObserverEntry) {
        if (entry) {
            updateVisibility(entry.target.clientWidth, entry.target.clientHeight)
        }
    }

    function handleOpenVouchersList() {
        if (!handleOpenExternalDialog(voucherType)) {
            history.push(url)
        }
    }

    function handleWidgetSVariantClick(entry: VoucherType) {
        const params = {
            ...matchParams,
            type: mapVoucherTypeForUrl(entry),
            workTaskId: matchParams.workTaskId ?? encodeUniqueId(uniqueId()),
        }
        const route = renderRoute(getBundleParams().vouchersListRoute, params)

        if (externalSystemId && entry === VoucherType.Order) {
            setShowExternalOrdersDialog(true)
        } else {
            history.push(route)
        }
        TmaHelper.GeneralCountEvent.Call(ECounterType.VoucherList)
    }

    function renderDialog() {
        if (externalSystemId) {
            return (
                <ContentDialog
                    onClose={() => setShowExternalOrdersDialog(false)}
                    open={showExternalOrdersDialog}
                    title={translateText(31)}
                    icon="voucher"
                >
                    <iframe src={externalCatalogUrl} title="External vouchers" width="100%" height="98%" />
                </ContentDialog>
            )
        }
    }

    if (showComponentKey && selectedMultiWidgetTab !== showComponentKey) {
        return null
    }

    if (showComponentKey) {
        return (
            <Stack order="2" height="100%" width="100%">
                {renderDialog()}
                <Box position="absolute" top={0} right={0}>
                    <VoucherWidgetHeader listActive={listActive} voucherType={voucherType} onVoucherTypeChanged={setVoucherType} />
                </Box>
                <Box flexGrow={1}>
                    <VoucherWidgetContent
                        voucherType={voucherType}
                        showName={showName}
                        url={url}
                        enabled={active && enableServiceCalls}
                        externalSystemId={externalSystemId}
                        showArticleNumberSearch={showArticleNumberSearch}
                        onOpenOrderVoucher={() => handleOpenExternalDialog(VoucherType.Order)}
                    />
                </Box>
                <Box>
                    <VoucherWidgetFooter
                        disabled={externalSystemId ? loadingExternalCatalogUrl : false}
                        showArticleNumberSearch={showArticleNumberSearch}
                        onOpenVouchersList={handleOpenVouchersList}
                    />
                </Box>
            </Stack>
        )
    }

    if (showSVariant) {
        return (
            <LegacyContainer className={className} size={size || "4x2"} height={height}>
                {renderDialog()}
                <WidgetSVariant
                    title={translateText(titleTextId || 31)}
                    icon="voucher"
                    selectedVoucherType={voucherType}
                    ref={combinedRef}
                    onChangeVoucherType={handleWidgetSVariantClick}
                />
            </LegacyContainer>
        )
    }

    return (
        <LegacyContainer className={className} size={size || "4x2"} height={height}>
            {renderDialog()}
            <SimpleWidgetCard
                ref={combinedRef}
                active={active}
                title={translateText(titleTextId || 31)}
                icon="voucher"
                handleCoverClick={handleCoverClick}
                headerAction={<VoucherWidgetHeader listActive={listActive} voucherType={voucherType} onVoucherTypeChanged={setVoucherType} />}
                footerAction={
                    <VoucherWidgetFooter
                        disabled={externalSystemId ? loadingExternalCatalogUrl : false}
                        showArticleNumberSearch={showArticleNumberSearch}
                        onOpenVouchersList={handleOpenVouchersList}
                    />
                }
                widgetVariant={workTask?.id ? "dashboard" : "startpage"}
            >
                <VoucherWidgetContent
                    voucherType={voucherType}
                    showName={showName}
                    url={url}
                    enabled={active && enableServiceCalls}
                    externalSystemId={externalSystemId}
                    showArticleNumberSearch={showArticleNumberSearch}
                    onOpenOrderVoucher={() => handleOpenExternalDialog(VoucherType.Order)}
                />
            </SimpleWidgetCard>
        </LegacyContainer>
    )
}

export function VoucherWidget(props: Omit<Props, "defaultVoucherType">) {
    const defaultVoucherType = useDefaultVoucherType()

    if (!defaultVoucherType) {
        return null
    }
    return <WidgetComponent {...props} defaultVoucherType={defaultVoucherType} />
}
